import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        return;
      }
      if (session?.user) {
        const prof = await fetchProfile();
        setUser(session.user);
        if (prof) setProfile(prof);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchProfile = async () => {
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 10000)
      );
      const rpcPromise = supabase.rpc('get_own_profile');
      const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);
      if (error) throw error;
      return data || null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const checkAuth = async () => {
    try {
      // getSession() lee del almacenamiento local — instantáneo, sin red
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const prof = await fetchProfile();
        setUser(session.user);
        setProfile(prof);
      } else {
        setUser(false);
        setProfile(null);
      }
    } catch (error) {
      setUser(false);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      await new Promise(resolve => setTimeout(resolve, 200));

      const prof = await fetchProfile();
      if (!prof) {
        await supabase.auth.signOut();
        return { success: false, error: 'No tienes permisos de administrador.' };
      }

      setUser(data.user);
      setProfile(prof);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Error al iniciar sesión' };
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SOLUCIÓN AL BUG DE CREACIÓN DE USUARIOS:
  //
  // Antes: usaba supabase.auth.signUp() en el cliente, que automáticamente
  // inicia sesión con el nuevo usuario, expulsando al super admin y
  // rebajando su rol visualmente.
  //
  // Ahora: llama a la función RPC create_admin_user() en el servidor.
  // Esta función crea el usuario directamente en auth.users mediante SQL
  // sin crear ninguna sesión en el cliente. La sesión del super admin
  // permanece intacta en todo momento.
  // ─────────────────────────────────────────────────────────────────────────
  const signup = async (email, password, name, role = 'admin', permissions = { view: true, download: false, edit: false }, allowedCountries = null) => {
    try {
      const { data, error } = await supabase.rpc('create_admin_user', {
        p_email:             email,
        p_password:          password,
        p_name:              name,
        p_role:              role,
        p_permissions:       permissions,
        p_allowed_countries: allowedCountries,
      });

      if (error) throw error;
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message || 'Error al crear usuario' };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(false);
      setProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const contextValue = {
    user: profile ? { ...profile, email: profile.email, _id: profile.id } : user,
    loading,
    login,
    signup,
    logout,
    checkAuth,
    profile,
    isSuper: profile?.role === 'super_admin',
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
